import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Palette, 
  Type, 
  MessageSquare, 
  Target, 
  Heart,
  Zap,
  Shield,
  Copy,
  Check,
  Download,
  Eye,
  Users,
  Sparkles,
  ArrowRight
} from 'lucide-react';

// Color swatch component
const ColorSwatch = ({ name, hex, rgb, usage, isPrimary }) => {
  const [copied, setCopied] = useState(false);
  
  const copyHex = async () => {
    await navigator.clipboard.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className={`rounded-xl overflow-hidden border ${isPrimary ? 'border-2 border-lime-500' : 'border-slate-200'}`}>
      <div 
        className="h-24 flex items-end p-3"
        style={{ backgroundColor: hex }}
      >
        {isPrimary && (
          <Badge className="bg-white/90 text-slate-900 text-xs">Primary</Badge>
        )}
      </div>
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between mb-1">
          <span className="font-semibold text-slate-900">{name}</span>
          <button 
            onClick={copyHex}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            {copied ? <Check className="h-4 w-4 text-lime-600" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <p className="font-mono text-sm text-slate-600">{hex}</p>
        <p className="font-mono text-xs text-slate-400">{rgb}</p>
        <p className="text-xs text-slate-500 mt-2">{usage}</p>
      </div>
    </div>
  );
};

// Typography sample component
const TypeSample = ({ name, className, sample, specs }) => (
  <div className="border-b border-slate-100 pb-6 last:border-0 last:pb-0">
    <div className="flex items-baseline justify-between mb-2">
      <span className="text-sm font-medium text-slate-500">{name}</span>
      <span className="text-xs font-mono text-slate-400">{specs}</span>
    </div>
    <p className={className}>{sample}</p>
  </div>
);

// Voice example component
const VoiceExample = ({ doText, dontText, context }) => (
  <div className="grid grid-cols-2 gap-4">
    <div className="p-4 bg-lime-50 rounded-lg border border-lime-200">
      <div className="flex items-center gap-2 mb-2">
        <Check className="h-4 w-4 text-lime-600" />
        <span className="text-sm font-medium text-lime-700">Do</span>
      </div>
      <p className="text-sm text-slate-700">{doText}</p>
    </div>
    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-red-600 font-bold">✕</span>
        <span className="text-sm font-medium text-red-700">Don't</span>
      </div>
      <p className="text-sm text-slate-700">{dontText}</p>
    </div>
    <p className="col-span-2 text-xs text-slate-500 italic">{context}</p>
  </div>
);

export default function BrandGuidePage() {
  const [activeSection, setActiveSection] = useState('overview');
  
  const sections = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'positioning', label: 'Positioning', icon: Target },
    { id: 'icp', label: 'ICP', icon: Users },
    { id: 'colors', label: 'Colors', icon: Palette },
    { id: 'typography', label: 'Typography', icon: Type },
    { id: 'voice', label: 'Voice & Tone', icon: MessageSquare },
    { id: 'values', label: 'Values', icon: Heart },
  ];

  const primaryColors = [
    { name: 'Evergreen 950', hex: '#022c22', rgb: 'RGB(2, 44, 34)', usage: 'Primary brand, headers, CTAs', isPrimary: true },
    { name: 'Evergreen 900', hex: '#064e3b', rgb: 'RGB(6, 78, 59)', usage: 'Hover states, secondary actions' },
    { name: 'Lime 600', hex: '#65a30d', rgb: 'RGB(101, 163, 13)', usage: 'Accent, success states, highlights', isPrimary: true },
    { name: 'Lime 500', hex: '#84cc16', rgb: 'RGB(132, 204, 22)', usage: 'Charts, progress indicators' },
    { name: 'Lime 100', hex: '#ecfccb', rgb: 'RGB(236, 252, 203)', usage: 'Light backgrounds, badges' },
  ];
  
  const neutralColors = [
    { name: 'Slate 900', hex: '#0f172a', rgb: 'RGB(15, 23, 42)', usage: 'Body text, primary content' },
    { name: 'Slate 600', hex: '#475569', rgb: 'RGB(71, 85, 105)', usage: 'Secondary text' },
    { name: 'Slate 400', hex: '#94a3b8', rgb: 'RGB(148, 163, 184)', usage: 'Placeholder, disabled' },
    { name: 'Slate 100', hex: '#f1f5f9', rgb: 'RGB(241, 245, 249)', usage: 'Backgrounds, dividers' },
    { name: 'White', hex: '#ffffff', rgb: 'RGB(255, 255, 255)', usage: 'Cards, primary backgrounds' },
  ];

  const semanticColors = [
    { name: 'Blue 600', hex: '#2563eb', rgb: 'RGB(37, 99, 235)', usage: 'Information, links' },
    { name: 'Amber 500', hex: '#f59e0b', rgb: 'RGB(245, 158, 11)', usage: 'Warnings, in-progress' },
    { name: 'Red 500', hex: '#ef4444', rgb: 'RGB(239, 68, 68)', usage: 'Errors, destructive actions' },
    { name: 'Purple 600', hex: '#9333ea', rgb: 'RGB(147, 51, 234)', usage: 'Premium, special features' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/varyynt-icon.svg" alt="Varyynt" className="h-8 w-8" />
              <span className="text-xl font-heading text-slate-900">Brand Guide</span>
              <Badge variant="outline" className="text-xs">v1.0</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700">Internal + Partners</Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex gap-12">
          {/* Sidebar Navigation */}
          <aside className="w-64 shrink-0">
            <nav className="sticky top-24 space-y-1">
              {sections.map(section => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-all ${
                      activeSection === section.id
                        ? 'bg-evergreen-950 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Overview Section */}
            {activeSection === 'overview' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4 flex items-center gap-3">
                    <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" /> Brand Guide
                  </h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    A comprehensive guide to our brand identity, voice, and visual language. 
                    Built for strategic partners and internal teams.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  <Card className="bg-gradient-to-br from-evergreen-950 to-evergreen-900 text-white border-0">
                    <CardContent className="p-6">
                      <Zap className="h-8 w-8 text-lime-400 mb-4" />
                      <h3 className="text-lg font-heading mb-2">Mission</h3>
                      <p className="text-white/80 text-sm">
                        Empower life sciences professionals to navigate their careers with clarity, 
                        connecting aspirations with opportunities.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-lime-200 bg-lime-50">
                    <CardContent className="p-6">
                      <Target className="h-8 w-8 text-lime-600 mb-4" />
                      <h3 className="text-lg font-heading text-slate-900 mb-2">Vision</h3>
                      <p className="text-slate-600 text-sm">
                        The definitive career platform for the life sciences industry—where 
                        talent meets purpose.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <Sparkles className="h-8 w-8 text-amber-500 mb-4" />
                      <h3 className="text-lg font-heading text-slate-900 mb-2">Promise</h3>
                      <p className="text-slate-600 text-sm">
                        We match you to where you're going, not just where you've been. 
                        Your future, prioritized.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-slate-50 rounded-2xl p-8">
                  <h2 className="text-2xl font-heading text-slate-900 mb-6">Brand Essence</h2>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">What We Are</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-slate-600">
                          <Check className="h-4 w-4 text-lime-600 mt-1 shrink-0" />
                          An AI-powered career agent for life sciences professionals
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <Check className="h-4 w-4 text-lime-600 mt-1 shrink-0" />
                          Candidate-centric—we work for the talent, not against them
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <Check className="h-4 w-4 text-lime-600 mt-1 shrink-0" />
                          Forward-looking—matching aspirations, not just experience
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <Check className="h-4 w-4 text-lime-600 mt-1 shrink-0" />
                          Privacy-first—your data, your control
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">What We're Not</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-slate-600">
                          <span className="text-red-500 mt-1 shrink-0">✕</span>
                          A job board where employers browse candidates
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <span className="text-red-500 mt-1 shrink-0">✕</span>
                          A recruiter tool that commoditizes talent
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <span className="text-red-500 mt-1 shrink-0">✕</span>
                          Backwards-looking—just parsing resumes for keywords
                        </li>
                        <li className="flex items-start gap-2 text-slate-600">
                          <span className="text-red-500 mt-1 shrink-0">✕</span>
                          Data-hungry—selling user information
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Positioning Section */}
            {activeSection === 'positioning' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4">Positioning</h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    How we occupy a unique space in the market and in the minds of our audience.
                  </p>
                </div>

                <Card className="border-2 border-evergreen-950">
                  <CardContent className="p-8">
                    <h2 className="text-sm font-semibold text-evergreen-950 uppercase tracking-wider mb-4">
                      Positioning Statement
                    </h2>
                    <p className="text-2xl font-heading text-slate-900 leading-relaxed">
                      For <span className="text-lime-600">life sciences professionals</span> who want 
                      to navigate their career with intention, <span className="text-lime-600">Varyynt</span> is 
                      the <span className="text-lime-600">AI-powered career platform</span> that matches 
                      opportunities to your aspirations—not just your resume. Unlike traditional job boards 
                      and recruiters, we put candidates first, keeping your profile private until you 
                      choose to engage.
                    </p>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-heading text-slate-900 mb-4">Category</h3>
                    <p className="text-slate-600 mb-4">
                      AI-powered career platform for life sciences
                    </p>
                    <div className="space-y-2">
                      <Badge className="bg-slate-100 text-slate-700 mr-2">Career Tech</Badge>
                      <Badge className="bg-slate-100 text-slate-700 mr-2">Life Sciences</Badge>
                      <Badge className="bg-slate-100 text-slate-700 mr-2">AI/ML</Badge>
                      <Badge className="bg-slate-100 text-slate-700">Talent</Badge>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-heading text-slate-900 mb-4">Competitive Frame</h3>
                    <p className="text-slate-600">
                      We compete with job boards (LinkedIn, Indeed), life sciences recruiters, 
                      and specialized platforms (BioSpace, Pharma Times). Our differentiation: 
                      candidate-first, aspiration-focused, privacy-respecting.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-900 text-white rounded-2xl p-8">
                  <h3 className="text-lg font-heading mb-6">Key Messages</h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <div className="text-lime-400 text-4xl font-heading mb-2">01</div>
                      <h4 className="font-semibold mb-2">Future-Focused</h4>
                      <p className="text-white/70 text-sm">
                        "We match you to where you're going, not just where you've been."
                      </p>
                    </div>
                    <div>
                      <div className="text-lime-400 text-4xl font-heading mb-2">02</div>
                      <h4 className="font-semibold mb-2">Candidate-Centric</h4>
                      <p className="text-white/70 text-sm">
                        "Your career agent. Your profile stays private until you decide."
                      </p>
                    </div>
                    <div>
                      <div className="text-lime-400 text-4xl font-heading mb-2">03</div>
                      <h4 className="font-semibold mb-2">Industry Expert</h4>
                      <p className="text-white/70 text-sm">
                        "Built for life sciences. We speak your language."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ICP Section */}
            {activeSection === 'icp' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4">Ideal Customer Profile</h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    Understanding who we serve—deeply and specifically.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <Card className="border-lime-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-lime-600" />
                        Primary Persona: The Ambitious Mover
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Demographics</h4>
                        <ul className="text-sm text-slate-600 space-y-1">
                          <li>• 28-45 years old</li>
                          <li>• 5-15 years in life sciences</li>
                          <li>• MS/PhD or MBA background</li>
                          <li>• Currently employed, open to opportunity</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Roles</h4>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="secondary">Clinical Development</Badge>
                          <Badge variant="secondary">Medical Affairs</Badge>
                          <Badge variant="secondary">Regulatory</Badge>
                          <Badge variant="secondary">Commercial</Badge>
                          <Badge variant="secondary">R&D Leadership</Badge>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Psychographics</h4>
                        <ul className="text-sm text-slate-600 space-y-1">
                          <li>• Wants to make an impact in healthcare</li>
                          <li>• Values career growth over job security</li>
                          <li>• Privacy-conscious, selective about exposure</li>
                          <li>• Prefers curated opportunities over job board noise</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-blue-600" />
                        Secondary Persona: The Strategic Transitioner
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Demographics</h4>
                        <ul className="text-sm text-slate-600 space-y-1">
                          <li>• 35-55 years old</li>
                          <li>• 15+ years experience</li>
                          <li>• Senior/Executive level</li>
                          <li>• Looking to pivot or level up</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Transition Goals</h4>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline">Pharma → Biotech</Badge>
                          <Badge variant="outline">Big Co → Startup</Badge>
                          <Badge variant="outline">Technical → Leadership</Badge>
                          <Badge variant="outline">US → Global</Badge>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Pain Points</h4>
                        <ul className="text-sm text-slate-600 space-y-1">
                          <li>• Tired of recruiter spam</li>
                          <li>• Doesn't want current employer to know they're looking</li>
                          <li>• Needs guidance on market positioning</li>
                          <li>• Values relationships over transactions</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
                  <h3 className="text-lg font-heading text-slate-900 mb-4">Jobs To Be Done</h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold text-amber-700 mb-2">Functional</h4>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li>• Find relevant opportunities without active searching</li>
                        <li>• Understand my market value</li>
                        <li>• Connect with the right companies</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-700 mb-2">Emotional</h4>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li>• Feel in control of my career narrative</li>
                        <li>• Reduce anxiety about missing opportunities</li>
                        <li>• Gain confidence in career decisions</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-700 mb-2">Social</h4>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li>• Be seen as a thought leader</li>
                        <li>• Build meaningful professional relationships</li>
                        <li>• Help others navigate their careers</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Colors Section */}
            {activeSection === 'colors' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4">Color Palette</h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    Our colors evoke growth, professionalism, and forward momentum. 
                    Evergreen for trust, lime for energy.
                  </p>
                </div>

                <div>
                  <h2 className="text-lg font-heading text-slate-900 mb-4">Primary Colors</h2>
                  <div className="grid grid-cols-5 gap-4">
                    {primaryColors.map(color => (
                      <ColorSwatch key={color.name} {...color} />
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-heading text-slate-900 mb-4">Neutral Colors</h2>
                  <div className="grid grid-cols-5 gap-4">
                    {neutralColors.map(color => (
                      <ColorSwatch key={color.name} {...color} />
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-heading text-slate-900 mb-4">Semantic Colors</h2>
                  <div className="grid grid-cols-4 gap-4">
                    {semanticColors.map(color => (
                      <ColorSwatch key={color.name} {...color} />
                    ))}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-8">
                  <h3 className="text-lg font-heading text-slate-900 mb-4">Color Usage Guidelines</h3>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-lime-700 mb-2">Do</h4>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li>• Use Evergreen 950 for primary CTAs and headers</li>
                        <li>• Use Lime 600 for accents and success states</li>
                        <li>• Maintain sufficient contrast for accessibility</li>
                        <li>• Use semantic colors consistently for their purpose</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-red-700 mb-2">Don't</h4>
                      <ul className="text-sm text-slate-600 space-y-2">
                        <li>• Don't use lime as a background for large areas</li>
                        <li>• Don't combine multiple bright colors</li>
                        <li>• Don't use red for non-destructive actions</li>
                        <li>• Don't use colors that compete with our palette</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Typography Section */}
            {activeSection === 'typography' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4">Typography</h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    Clean, modern, and highly readable. Two font families for distinction and clarity.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <Card>
                    <CardHeader>
                      <CardTitle>Heading Font</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-5xl font-heading text-slate-900 mb-4">Poppins</p>
                      <p className="text-sm text-slate-600 mb-4">
                        Semi-bold (600) weight. Used for headlines, section titles, 
                        and brand moments. Geometric, modern, confident.
                      </p>
                      <div className="font-mono text-xs text-slate-400 bg-slate-50 p-3 rounded">
                        font-family: 'Poppins', sans-serif;<br/>
                        font-weight: 600;
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Body Font</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-5xl text-slate-900 mb-4">Inter</p>
                      <p className="text-sm text-slate-600 mb-4">
                        Regular (400) and Medium (500) weights. Used for body copy, 
                        UI elements, and data. Highly legible at all sizes.
                      </p>
                      <div className="font-mono text-xs text-slate-400 bg-slate-50 p-3 rounded">
                        font-family: 'Inter', sans-serif;<br/>
                        font-weight: 400 | 500;
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <h2 className="text-lg font-heading text-slate-900 mb-6">Type Scale</h2>
                  <div className="space-y-6">
                    <TypeSample 
                      name="Display" 
                      className="text-5xl font-heading text-slate-900"
                      sample="The future of life sciences careers"
                      specs="48px / 600 / Poppins"
                    />
                    <TypeSample 
                      name="H1" 
                      className="text-4xl font-heading text-slate-900"
                      sample="Your career, reimagined"
                      specs="36px / 600 / Poppins"
                    />
                    <TypeSample 
                      name="H2" 
                      className="text-2xl font-heading text-slate-900"
                      sample="Build your career profile"
                      specs="24px / 600 / Poppins"
                    />
                    <TypeSample 
                      name="H3" 
                      className="text-lg font-heading text-slate-900"
                      sample="Complete your preferences"
                      specs="18px / 600 / Poppins"
                    />
                    <TypeSample 
                      name="Body Large" 
                      className="text-lg text-slate-600"
                      sample="We match opportunities to your aspirations, not just your resume."
                      specs="18px / 400 / Inter"
                    />
                    <TypeSample 
                      name="Body" 
                      className="text-base text-slate-600"
                      sample="Select the industries that align with your career goals and interests."
                      specs="16px / 400 / Inter"
                    />
                    <TypeSample 
                      name="Body Small" 
                      className="text-sm text-slate-600"
                      sample="Your profile is private by default. You control when and how you're discovered."
                      specs="14px / 400 / Inter"
                    />
                    <TypeSample 
                      name="Caption" 
                      className="text-xs text-slate-500"
                      sample="Last updated 2 hours ago"
                      specs="12px / 400 / Inter"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Voice & Tone Section */}
            {activeSection === 'voice' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4">Voice & Tone</h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    How we communicate—confident without arrogance, warm without being casual, 
                    smart without being academic.
                  </p>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="text-4xl mb-3">🎯</div>
                      <h3 className="font-semibold text-slate-900 mb-2">Direct</h3>
                      <p className="text-sm text-slate-600">Clear, concise, no jargon for jargon's sake</p>
                    </CardContent>
                  </Card>
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="text-4xl mb-3">🤝</div>
                      <h3 className="font-semibold text-slate-900 mb-2">Supportive</h3>
                      <p className="text-sm text-slate-600">We're on your side, your career agent</p>
                    </CardContent>
                  </Card>
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="text-4xl mb-3">💡</div>
                      <h3 className="font-semibold text-slate-900 mb-2">Informed</h3>
                      <p className="text-sm text-slate-600">Industry expertise, not generic advice</p>
                    </CardContent>
                  </Card>
                  <Card className="text-center">
                    <CardContent className="p-6">
                      <div className="text-4xl mb-3">✨</div>
                      <h3 className="font-semibold text-slate-900 mb-2">Optimistic</h3>
                      <p className="text-sm text-slate-600">Forward-looking, possibility-focused</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-8">
                  <h2 className="text-2xl font-heading text-slate-900">Voice Examples</h2>
                  
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Headlines & Taglines</h3>
                    <VoiceExample 
                      doText="Your career, reimagined"
                      dontText="Revolutionary AI-powered career transformation platform"
                      context="Keep it human and aspirational, not tech-heavy."
                    />
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">CTAs</h3>
                    <VoiceExample 
                      doText="Start Building Your Profile"
                      dontText="Click Here to Begin Registration Process"
                      context="Action-oriented, benefit-focused, never bureaucratic."
                    />
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Error Messages</h3>
                    <VoiceExample 
                      doText="That email is already registered. Try logging in instead?"
                      dontText="Error 409: Duplicate entry in user table."
                      context="Helpful, human, solution-oriented."
                    />
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Success States</h3>
                    <VoiceExample 
                      doText="You're all set! We'll notify you when opportunities match your preferences."
                      dontText="Profile submission successful. Data has been saved to the database."
                      context="Celebrate the moment, then point forward."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Values Section */}
            {activeSection === 'values' && (
              <div className="space-y-12">
                <div>
                  <h1 className="text-4xl font-heading text-slate-900 mb-4">Brand Values</h1>
                  <p className="text-xl text-slate-600 max-w-2xl">
                    The principles that guide every decision, feature, and communication.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <Card className="border-l-4 border-l-lime-500">
                    <CardContent className="p-6">
                      <Shield className="h-8 w-8 text-lime-600 mb-4" />
                      <h3 className="text-xl font-heading text-slate-900 mb-2">Privacy First</h3>
                      <p className="text-slate-600 mb-4">
                        Your profile is yours. You decide when and how to share it. 
                        No employer browsing, no data selling, no surprises.
                      </p>
                      <div className="text-sm text-slate-500">
                        <strong>In practice:</strong> Profiles are never searchable by employers. 
                        Candidates initiate all connections.
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-6">
                      <Target className="h-8 w-8 text-blue-600 mb-4" />
                      <h3 className="text-xl font-heading text-slate-900 mb-2">Aspiration Over History</h3>
                      <p className="text-slate-600 mb-4">
                        We care about where you want to go, not just where you've been. 
                        Your future potential matters more than your past titles.
                      </p>
                      <div className="text-sm text-slate-500">
                        <strong>In practice:</strong> Matching algorithms prioritize stated goals 
                        and interests over resume keywords.
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="p-6">
                      <Heart className="h-8 w-8 text-amber-600 mb-4" />
                      <h3 className="text-xl font-heading text-slate-900 mb-2">Candidate Advocacy</h3>
                      <p className="text-slate-600 mb-4">
                        We work for you, not against you. No hidden agendas, no playing 
                        both sides. Your success is our success.
                      </p>
                      <div className="text-sm text-slate-500">
                        <strong>In practice:</strong> Business model aligns with candidate outcomes, 
                        not recruiter volume.
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="p-6">
                      <Sparkles className="h-8 w-8 text-purple-600 mb-4" />
                      <h3 className="text-xl font-heading text-slate-900 mb-2">Industry Expertise</h3>
                      <p className="text-slate-600 mb-4">
                        We speak life sciences. Built by people who understand the industry, 
                        for people who live it.
                      </p>
                      <div className="text-sm text-slate-500">
                        <strong>In practice:</strong> Specialized taxonomies, relevant role categories, 
                        industry-specific career paths.
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-evergreen-950 text-white rounded-2xl p-8">
                  <h2 className="text-2xl font-heading mb-6">The Ogilvy Test</h2>
                  <p className="text-white/80 mb-6">
                    David Ogilvy said: "The consumer isn't a moron; she is your wife." 
                    We apply this to every piece of communication:
                  </p>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <h4 className="text-lime-400 font-semibold mb-2">Would you say it to a colleague?</h4>
                      <p className="text-white/70 text-sm">
                        If it sounds like marketing speak, rewrite it.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-lime-400 font-semibold mb-2">Does it respect their intelligence?</h4>
                      <p className="text-white/70 text-sm">
                        Our users are PhD-level scientists and executives. Write accordingly.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-lime-400 font-semibold mb-2">Is it true?</h4>
                      <p className="text-white/70 text-sm">
                        Never promise what we can't deliver. Underpromise, overdeliver.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/varyynt-icon.svg" alt="Varyynt" className="h-8 w-8" />
              <span className="text-sm text-slate-500">
                Varyynt Brand Guide • Internal + Strategic Partners Only
              </span>
            </div>
            <p className="text-sm text-slate-400">
              Last updated: January 2026
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
